import * as React from 'react';
import { LuCheck } from 'react-icons/lu';

import { Field } from './form';
import { Box } from './box';

export interface CheckboxProps {
  label?: string;
  error?: string;
  hint?: string;
  description?: string;
  containerClassName?: string;
  checkboxSize?: 'sm' | 'md' | 'lg';
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  children?: React.ReactNode;
  id?: string;
  defaultChecked?: boolean;
  checked?: boolean | 'indeterminate';
  onChange?: (checked: boolean) => void;
  onCheckedChange?: (details: { checked: boolean | 'indeterminate' }) => void;
}

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>((props, ref) => {
  const {
    label,
    error,
    hint,
    description,
    containerClassName,
    checkboxSize = 'md',
    required,
    disabled,
    readOnly,
    children,
    id,
    checked,
    defaultChecked,
    onChange,
    onCheckedChange,
  } = props;

  // Generate ID for accessibility
  const generatedId = React.useId();
  const checkboxId = id || generatedId;

  // Determine if component is controlled (checked prop is explicitly provided)
  const isControlled = checked !== undefined;

  // Internal state for uncontrolled mode
  const [internalChecked, setInternalChecked] = React.useState(defaultChecked ?? false);

  // Actual checked value - use prop if controlled, internal state if uncontrolled
  const actualChecked = isControlled ? checked : internalChecked;

  // Handle change
  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newChecked = e.target.checked;

      // Update internal state for uncontrolled mode
      if (!isControlled) {
        setInternalChecked(newChecked);
      }

      // Call onCheckedChange if provided
      if (onCheckedChange) {
        onCheckedChange({ checked: newChecked });
      }

      // Call onChange if provided (for backward compatibility)
      if (onChange) {
        onChange(newChecked);
      }
    },
    [isControlled, onChange, onCheckedChange]
  );

  // Determine if indeterminate
  const isIndeterminate = actualChecked === 'indeterminate';

  // Map sizes
  const sizeMap = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  // Touch-friendly logic - make checkbox easier to tap on mobile
  const controlSizes = {
    sm: 16, // Actual pixel size
    md: 20,
    lg: 24,
  };

  const minTouchSize = 44; // Apple's recommended minimum touch target size
  const controlSize = controlSizes[checkboxSize] || 20;
  const needsTouchPadding = controlSize < minTouchSize;
  const touchPadding = needsTouchPadding ? (minTouchSize - controlSize) / 2 : 0;

  const checkboxClasses = `
    ${sizeMap[checkboxSize]}
    ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
    border-2 rounded
    flex items-center justify-center
    transition-colors duration-200
    shrink-0
    ${error ? 'border-red-500' : 'border-gray-300 hover:border-gray-400'}
    ${actualChecked === true ? 'bg-blue-600 border-blue-600' : 'bg-white'}
    ${isIndeterminate ? 'bg-blue-600 border-blue-600' : ''}
  `;

  // If only children provided without label, use simplified version
  if (children && !label && !error && !hint && !description) {
    const checkboxControl = (
      <div className={checkboxClasses} data-testid="checkbox-control">
        {(actualChecked === true || isIndeterminate) && (
          <span data-testid="check-icon" className="text-white">
            <LuCheck size={checkboxSize === 'sm' ? 14 : checkboxSize === 'md' ? 16 : 20} />
          </span>
        )}
      </div>
    );

    // Wrap with touch-friendly padding if needed
    if (needsTouchPadding) {
      return (
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            ref={ref}
            type="checkbox"
            id={checkboxId}
            checked={actualChecked === true}
            onChange={handleChange}
            disabled={disabled}
            readOnly={readOnly}
            className="sr-only"
            data-testid="checkbox-hidden-input"
          />
          <Box
            as="span"
            display="inline-block"
            position="relative"
            padding={`${touchPadding}px`}
            data-testid="checkbox-touch-wrapper"
          >
            {checkboxControl}
          </Box>
          <span className="text-sm text-gray-700 select-none">{children}</span>
        </label>
      );
    }

    return (
      <label className="inline-flex items-center gap-2 cursor-pointer">
        <input
          ref={ref}
          type="checkbox"
          id={checkboxId}
          checked={actualChecked === true}
          onChange={handleChange}
          disabled={disabled}
          readOnly={readOnly}
          className="sr-only"
          data-testid="checkbox-hidden-input"
        />
        {checkboxControl}
        <span className="text-sm text-gray-700 select-none">{children}</span>
      </label>
    );
  }

  const checkboxControl = (
    <div className={checkboxClasses} data-testid="checkbox-control">
      {(actualChecked === true || isIndeterminate) && (
        <span data-testid="check-icon" className="text-white">
          <LuCheck size={checkboxSize === 'sm' ? 14 : checkboxSize === 'md' ? 16 : 20} />
        </span>
      )}
    </div>
  );

  // Apply touch-friendly wrapper if needed
  const checkboxWithPadding = needsTouchPadding ? (
    <Box
      as="span"
      display="inline-block"
      position="relative"
      padding={`${touchPadding}px`}
      data-testid="checkbox-touch-wrapper"
    >
      {checkboxControl}
    </Box>
  ) : (
    checkboxControl
  );

  return (
    <Field.Root
      className={containerClassName}
      disabled={disabled}
      data-testid="field-root"
      {...(error ? { 'data-invalid': true } : {})}
    >
      <label className="inline-flex items-start gap-2 cursor-pointer">
        <input
          ref={ref}
          type="checkbox"
          id={checkboxId}
          checked={actualChecked === true}
          onChange={handleChange}
          disabled={disabled}
          readOnly={readOnly}
          className="sr-only"
          data-testid="checkbox-hidden-input"
        />
        {checkboxWithPadding}
        <div className="flex-1">
          {label && (
            <span
              className={`text-sm font-medium select-none ${error ? 'text-red-500' : 'text-gray-700'}`}
            >
              {label}
              {required && <span className="text-red-500 ml-1">*</span>}
            </span>
          )}
          {children && <span className="text-sm text-gray-700 select-none">{children}</span>}
        </div>
      </label>

      {/* Description */}
      {description && !error && !hint && (
        <div className="text-xs text-gray-600 ml-7 mt-1">{description}</div>
      )}

      {/* Error Message */}
      {error && <div className="text-xs text-red-500 ml-7 mt-1">{error}</div>}

      {/* Hint Message */}
      {hint && !error && <div className="text-xs text-gray-600 ml-7 mt-1">{hint}</div>}
    </Field.Root>
  );
});

Checkbox.displayName = 'Checkbox';

export { Checkbox };
